import React, { useState, useEffect } from "react";
import { BookOpen, Search, Loader2, LibraryBig, ChevronLeft, ChevronRight, X, Bookmark, BookmarkCheck, Share2, RotateCcw, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { OLD_TESTAMENT, NEW_TESTAMENT, CHAPTER_COUNT } from "@/lib/bibleBooks";
import { shareVerseImage } from "@/lib/verseImage";
import { useAuth } from "@/context/AuthContext";

const TRANSLATIONS = [
  { id: "kjv", label: "King James (KJV)" },
  { id: "web", label: "World English (WEB)" },
  { id: "webbe", label: "World English British (WEBBE)" },
  { id: "asv", label: "American Standard (ASV)" },
  { id: "darby", label: "Darby Bible" },
  { id: "ylt", label: "Young's Literal (YLT)" },
  { id: "dra", label: "Douay-Rheims (DRA)" },
  { id: "oeb-us", label: "Open English Bible (OEB)" },
];

const SUGGESTIONS = ["John 3:16", "Psalm 23", "Romans 8:28", "Philippians 4:6-7", "Isaiah 40:31", "Proverbs 3:5-6"];

export default function Bible() {
  const { user } = useAuth();
  const [reference, setReference] = useState("");
  const [translation, setTranslation] = useState("kjv");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBooks, setShowBooks] = useState(false);
  const [activeBook, setActiveBook] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/bible/bookmarks").then((r) => setBookmarks(r.data)).catch(() => {});
    api.get("/bible/progress").then((r) => { if (r.data?.book) setProgress(r.data); }).catch(() => {});
  }, [user]);

  const lookup = async (ref) => {
    const q = (ref ?? reference).trim();
    if (!q) return;
    if (ref) setReference(ref);
    setActiveBook(null); setActiveChapter(null);
    setLoading(true);
    try {
      const { data } = await api.get("/bible", { params: { reference: q, translation } });
      setResult(data);
    } catch (e) {
      setResult(null);
      toast.error(e.response?.data?.detail || "Could not find that passage");
    } finally { setLoading(false); }
  };

  const openChapter = async (book, chapter) => {
    setShowBooks(false);
    setLoading(true);
    setReference(`${book} ${chapter}`);
    try {
      const { data } = await api.get("/bible", { params: { reference: `${book} ${chapter}`, translation } });
      setResult(data);
      setActiveBook(book);
      setActiveChapter(chapter);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (user) api.post("/bible/progress", { book, chapter, translation }).then(() => setProgress({ book, chapter, translation })).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load that chapter");
    } finally { setLoading(false); }
  };

  const saveBookmark = async () => {
    if (!user) { toast.error("Sign in to save verses"); return; }
    if (!result) return;
    try {
      const { data } = await api.post("/bible/bookmarks", { reference: result.reference, translation, preview: result.text });
      setBookmarks((b) => [data, ...b.filter((x) => x.reference !== data.reference)]);
      toast.success("Verse bookmarked 🔖");
    } catch (e) { toast.error("Could not save"); }
  };

  const removeBookmark = async (id) => {
    setBookmarks((b) => b.filter((x) => x.id !== id));
    await api.delete(`/bible/bookmarks/${id}`).catch(() => {});
  };

  const shareImage = async () => {
    if (!result) return;
    try {
      const r = await shareVerseImage({ text: result.text.slice(0, 400), reference: result.reference });
      if (r === "downloaded") toast.success("Verse image downloaded");
    } catch (e) {}
  };

  const isBookmarked = result && bookmarks.some((b) => b.reference === result.reference);
  const totalChapters = activeBook ? CHAPTER_COUNT[activeBook] : 0;

  const BookGrid = ({ title, books }) => (
    <div className="mb-6">
      <p className="overline text-[#E6B34A] mb-3">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {books.map(([name]) => (
          <button key={name} onClick={() => openChapter(name, 1)} data-testid={`book-${name.replace(/\s+/g, "-").toLowerCase()}`}
            className="text-left px-3 py-2.5 rounded-xl bg-white/5 hover:bg-[#E6B34A]/15 hover:text-[#E6B34A] text-sm font-semibold transition-colors truncate">
            {name}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <BookOpen className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Read the Bible</h1>
      </div>
      <p className="text-zinc-400 mb-8">Look up any passage — e.g. "John 3:16" — or browse all 66 books online.</p>

      {progress && (
        <button onClick={() => openChapter(progress.book, progress.chapter)} data-testid="resume-reading" className="w-full sm:w-auto inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E6B34A]/10 border border-[#E6B34A]/30 text-[#E6B34A] font-semibold hover:bg-[#E6B34A]/20 transition-colors mb-5">
          <RotateCcw className="w-4 h-4" /> Continue reading — {progress.book} {progress.chapter}
        </button>
      )}

      <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="bible-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Enter a passage…" data-testid="bible-reference" className="pl-10 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-12" />
        </div>
        <select value={translation} onChange={(e) => setTranslation(e.target.value)} data-testid="bible-translation" className="rounded-xl bg-[#1E2A5A] border border-white/10 px-4 h-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]">
          {TRANSLATIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button type="submit" disabled={loading} data-testid="bible-submit" className="h-12 px-6 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Read"}
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        <button onClick={() => setShowBooks((v) => !v)} data-testid="show-all-books" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34] transition-colors">
          <LibraryBig className="w-4 h-4" /> {showBooks ? "Hide books" : "Show all books"}
        </button>
        <span className="text-zinc-600 text-sm mx-1">or</span>
        {SUGGESTIONS.map((s) => (
          <button key={s} onClick={() => lookup(s)} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/5 text-zinc-300 hover:bg-white/10 transition-colors">{s}</button>
        ))}
      </div>

      {showBooks && (
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6 mb-8 relative" data-testid="all-books">
          <button onClick={() => setShowBooks(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          <BookGrid title="Old Testament" books={OLD_TESTAMENT} />
          <BookGrid title="New Testament" books={NEW_TESTAMENT} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#E6B34A]" /></div>
      ) : result ? (
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6 lg:p-8" data-testid="bible-result">
          <div className="flex items-baseline justify-between gap-3 mb-5">
            <h2 className="font-display font-black text-2xl tracking-tight text-[#E6B34A]" data-testid="bible-result-ref">{result.reference}</h2>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={saveBookmark} data-testid="bookmark-verse" title="Bookmark" className={`p-2 rounded-lg transition-colors ${isBookmarked ? "text-[#E6B34A] bg-[#E6B34A]/10" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              </button>
              <button onClick={shareImage} data-testid="bible-share-image" title="Share as image" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
              <span className="overline text-zinc-500 ml-1">{result.translation}</span>
            </div>
          </div>
          <div className="space-y-3 leading-relaxed" data-testid="bible-verses">
            {result.verses.map((v, i) => (
              <p key={i} className="text-zinc-200">
                <sup className="text-[#E6B34A] font-bold mr-1.5">{v.verse}</sup>{v.text}
              </p>
            ))}
          </div>

          {activeBook && (
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-white/5" data-testid="chapter-nav">
              <button disabled={activeChapter <= 1} onClick={() => openChapter(activeBook, activeChapter - 1)} data-testid="prev-chapter"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold disabled:opacity-40 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <span className="text-sm text-zinc-400">{activeBook} {activeChapter} <span className="text-zinc-600">/ {totalChapters}</span></span>
              <button disabled={activeChapter >= totalChapters} onClick={() => openChapter(activeBook, activeChapter + 1)} data-testid="next-chapter"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold disabled:opacity-40 transition-colors">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="bible-empty">Search a passage or tap "Show all books" to start reading.</p>
      )}

      {user && bookmarks.length > 0 && (
        <div className="mt-10" data-testid="bookmarks-section">
          <h2 className="font-display font-extrabold text-lg tracking-tight mb-4 flex items-center gap-2"><Bookmark className="w-5 h-5 text-[#E6B34A]" /> Your Bookmarks</h2>
          <div className="space-y-2">
            {bookmarks.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#141C42] px-4 py-3" data-testid={`bookmark-${b.id}`}>
                <button onClick={() => lookup(b.reference)} className="flex-1 min-w-0 text-left">
                  <p className="font-semibold text-[#E6B34A] text-sm">{b.reference} <span className="text-zinc-600 text-xs uppercase">{b.translation}</span></p>
                  {b.preview && <p className="text-xs text-zinc-400 truncate mt-0.5">{b.preview}</p>}
                </button>
                <button onClick={() => removeBookmark(b.id)} data-testid={`remove-bookmark-${b.id}`} className="p-1.5 rounded-lg text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
